import React, {
  PropTypes as T,
  Component,
} from 'react';
import radium from 'radium';
import { connect } from 'react-redux';
const AudioContext = window.webkitAudioContext || window.AudioContext;

// Sounds
import woman from '../../sounds/woman.mp3';
import man from '../../sounds/man.mp3';
import child from '../../sounds/child.mp3';

// Component
import Switch from '../../components/Switch.js';

// Libs
import equalizer from '../../libs/equalizer.js';
import record from '../../libs/record.js';

// Hz Array
const soundHz = [125, 250, 300, 500, 1000, 2000, 4000, 8000];

export const ON = Symbol('ON');
export const OFF = Symbol('OFF');

const styles = {
  footerWrap: {
    width: '100%',
    textAlign: 'center',
  },
  title: {
    color: '#A39671',
    letterSpacing: 2.5,
    fontSize: 15,
    display: 'block',
    margin: '10px 0',
  },
  text: {
    fontSize: 12,
    color: 'white',
    display: 'block',
    margin: '5px 0px',
    letterSpacing: 1.5,
  },
  buttonWrap: {
    textAlign: 'center',
    margin: '12px 0 0 0',
  },
  button: {
    width: 260,
    lineHeight: '42px',
    outline: 'none',
    padding: 0,
    border: 0,
    borderRadius: 3,
    cursor: 'pointer',
    backgroundColor: '#9B9B9B',
    color: 'white',
    letterSpacing: 4,
    fontSize: 15,
  },
  playWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  play: {
    margin: '10px 0',
    padding: '0 40px',
    boxSizing: 'border-box',
    lineHeight: '20px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '@media (max-width: 767px)': {
      padding: '0px 10px',
    },
  },
  playIcon: {
    color: 'white',
    fontSize: 20,
    margin: '0px 15px 0 0',
  },
  playText: {
    color: 'white',
    fontSize: 22,
    letterSpacing: 7,
    '@media (max-width: 767px)': {
      fontSize: 15,
    },
  },
  playBorder: {
    borderLeft: '1px solid #9B9B9B',
    borderRight: '1px solid #9B9B9B',
  },
  recordTitle: {
    '@media (max-width: 767px)': {
      display: 'block',
      width: '100%',
      margin: '10px 0',
      textAlign: 'left',
    },
  },
  controller: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 42px 0 24px',
    margin: '16px 0 12px 0',
    '@media (max-width: 767px)': {
      flexWrap: 'wrap',
    },
  },
  controlBtn: {
    color: '#fff',
    fontSize: 40,
    width: 40,
    backgroundColor: 'transparent',
    border: 0,
    outline: 'none',
    padding: 0,
    margin: '0 18px',
    cursor: 'pointer',
    opacity: 0.75,
    ':hover': {
      opacity: 0.96,
    },
  },
  progressBar: {
    display: 'flex',
    position: 'relative',
    width: 300,
    height: 32,
    padding: '15px 0',
    backgroundColor: '#fff',
    borderRadius: 1,
    backgroundClip: 'content-box',
    '@media (max-width: 767px)': {
      width: 100,
    },
  },
  progressPan: {
    cursor: 'pointer',
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'block',
    borderRadius: 16,
    width: 32,
    height: 32,
    border: 0,
    outline: 'none',
    backgroundColor: '#FFF',
  },
  recordCircle: {
    position: 'relative',
  },
  recordBtn: {
    position: 'absolute',
    right: 0,
    left: 0,
    color: 'red',
    fontSize: 15,
    lineHeight: '40px',
  },
  switch: {
    display: 'block',
  },
  hide: {
    display: 'none',
  },
};

class SimulatorPlayer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      playing: false,
      volume: 0,
      isLeftEar: true,
      isRightEar: true,
      buffer: null,
      recordSource: null,
      audioNode: null,
      type: null,
      supportRecord: this.checkSupportNaviMedia(),
    };
  }

  componentWillUnmount() {
    if (this.state.audioNode) this.state.audioNode.stop();
  }

  mouseDown(panStartX) {
    const {
      muted,
    } = this.state;

    if (muted) {
      this.setState({
        panStartX,
        volume: 0,
        muted: false,
      });
    } else {
      this.setState({
        panStartX,
      });
    }
  }

  mouseMove(x) {
    const {
      volume,
      panStartX,
      barUnit,
    } = this.state;

    if (!panStartX) return;

    const {
      pan,
      bar,
      video,
    } = this.refs;

    const startX = panStartX - (barUnit * volume) - 16;
    const tempX = Math.min(Math.max(x - startX - 32, -16), 284);
    const tempVolume = (tempX + 16) / barUnit;

    let barStyle = `linear-gradient(to right, #fbbb0c 0, #fbbb0c ${tempVolume}%`;
    barStyle += `, transparent ${tempVolume}%, transparent 100%)`;

    bar.style.backgroundImage = barStyle;
    pan.style.left = `${tempX}px`;
    video.volume = tempVolume / 100;
  }

  mouseUp() {
    const {
      panStartX,
    } = this.state;

    if (!panStartX) return;

    const {
      pan,
    } = this.refs;

    const nowX = parseInt(pan.style.left.replace(/px$/, ''), 10);
    const volume = (nowX + 16) / 3;

    this.setState({
      panStartX: null,
      volume,
      muted: !volume,
    });
  }

  async togglePlayState() {
    if (this.state.playing) {
      this.state.recordSource.stop();
      clearInterval(this.loopProcess);
      this.setState({
        volume: 0,
      });
    } else {
      if (this.state.buffer) {
        const {
          left,
          right,
        } = this.props;

        const {
          isLeftEar,
          isRightEar,
        } = this.state;

        const setting = {
          left: [0, 0, 0, 0, 0, 0, 0, 0],
          right: [0, 0, 0, 0, 0, 0, 0, 0],
        };

        if (isLeftEar) {
          setting.left = left.map((gain, idx) => (
            [soundHz[idx], gain / 60]
          ));
        }

        if (isRightEar) {
          setting.right = right.map((gain, idx) => (
            [soundHz[idx], gain / 60]
          ));
        }
        try {
          const source = await equalizer(null, setting, this.state.buffer);
          source.start();
          this.loopBar();
          this.setState({
            recordSource: source,
          });
        } catch (ex) {
          alert('此功能不支援您的瀏覽器');
        }
      } else {
        alert('尚未錄製聲音!');
        return;
      }
    }

    this.setState({
      playing: !this.state.playing,
    });
  }

  toggleMuteState() {
    const {
      video,
    } = this.refs;

    if (this.state.muted) {
      video.muted = false;
    } else {
      video.muted = true;
    }

    this.setState({
      muted: !this.state.muted,
    });
  }

  videoOn() {
    const {
      video,
    } = this.refs;

    const {
      mode,
    } = this.state;

    if (mode === ON) return;

    this.setState({
      mode: ON,
      lastTime: video.currentTime,
    });
  }

  videoOff() {
    const {
      video,
    } = this.refs;

    const {
      mode,
    } = this.state;

    if (mode === OFF) return;

    this.setState({
      mode: OFF,
      lastTime: video.currentTime,
    });
  }

  async play(type) {
    if (type === this.state.type && this.state.playing) {
      if (this.state.audioNode) this.state.audioNode.stop();
      this.setState({
        playing: false,
        type: null,
        audioNode: null,
      });
      return;
    }
    const {
      left,
      right,
      isLeftEar,
      isRightEar,
    } = this.props;

    console.log(left);

    const setting = {
      left: [],
      right: [],
    };

    if (isLeftEar) {
      setting.left = left.map((gain, idx) => (
        [soundHz[idx], gain / 60]
      ));
    } else {
      setting.left = left.map((gain, idx) => (
        [soundHz[idx], 0]
      ));
    }

    if (isRightEar) {
      setting.right = right.map((gain, idx) => (
        [soundHz[idx], gain / 60]
      ));
    } else {
      setting.right = right.map((gain, idx) => (
        [soundHz[idx], 0]
      ));
    }

    let source = null;
    switch (type) {
      case 'woman':
        source = await equalizer(woman, setting);
        break;
      case 'man':
        source = await equalizer(man, setting);
        break;
      case 'child':
        source = await equalizer(child, setting);
        break;

      default:

    }

    if (source) {
      if (this.state.audioNode) this.state.audioNode.stop();
      source.start(0);

      this.setState({
        audioNode: source,
        playing: true,
        type,
      });
    }
  }

  async initAudio() {
    // change process bar
    this.changeProcess();

    try {
      const buffer = await record();
      this.setState({
        buffer,
        type: 'record',
      });
    } catch (ex) {
      alert('此功能不支援您的瀏覽器');
    }
  }

  changeProcess() {
    this.process = setInterval(() => this.setState({
      volume: this.state.volume + 20,
    }), 1000);
    setTimeout(() => {
      clearInterval(this.process);
      setTimeout(() => {
        this.setState({
          volume: 0,
        });
      }, 1000);
    }, 5000);
  }

  loopBar() {
    this.setState({
      volume: this.state.volume + 20,
    });
    this.loopProcess = setInterval(() => {
      if (this.state.volume >= 100) {
        this.setState({
          volume: 0,
        });
      } else {
        this.setState({
          volume: this.state.volume + 20,
        });
      }
    }, 1000);
  }

  checkSupportNaviMedia() {
    navigator.getUserMedia = (navigator.getUserMedia ||
                              navigator.mozGetUserMedia ||
                              navigator.msGetUserMedia ||
                              navigator.webkitGetUserMedia);

    return !!navigator.getUserMedia || !AudioContext;
  }

  render() {
    const {
      isLeftEar,
      isRightEar,
      switchFunc,
    } = this.props;

    const {
      muted,
      volume,
      playing,
      supportRecord,
      type,
    } = this.state;

    const actualVolume = muted ? 0 : volume;
    let barStyle = `linear-gradient(to right, #fbbb0c 0, #fbbb0c ${actualVolume}%`;
    barStyle += `, transparent ${actualVolume}%, transparent 100%)`;
    return (
      <div style={styles.footerWrap}>
        <div style={styles.playWrap}>
          <div style={styles.play} onClick={() => this.play('man')}>
            <span style={styles.playIcon} className={`fa ${playing && type === 'man' ? 'fa-pause' : 'fa-play'}`} />
            <span style={styles.playText}>男聲</span>
          </div>
          <div style={[styles.play, styles.playBorder]} onClick={() => this.play('woman')}>
            <span style={styles.playIcon} className={`fa ${playing && type === 'woman' ? 'fa-pause' : 'fa-play'}`} />
            <span style={styles.playText}>女聲</span>
          </div>
          <div style={styles.play} onClick={() => this.play('child')}>
            <span style={styles.playIcon} className={`fa ${playing && type === 'child' ? 'fa-pause' : 'fa-play'}`} />
            <span style={styles.playText}>小孩聲</span>
          </div>
        </div>
        <div style={[styles.controller, !supportRecord && styles.hide]}>
          <span style={[styles.playText, styles.recordTitle]}>錄製現場聲音</span>
          <button type="button" style={styles.controlBtn} key="record" onClick={() => this.initAudio()}>
            <span style={styles.recordCircle} className="fa fa-circle-thin">
              <span style={styles.recordBtn} className={`fa ${playing ? 'fa-circle' : 'fa-square'}`} />
            </span>
          </button>
          <button type="button" style={styles.controlBtn} key="play" onClick={() => this.togglePlayState()}>
            <span className={`fa ${playing && type === 'record' ? 'fa-pause' : 'fa-play'}`} />
          </button>
          <div
            ref="bar"
            onMouseMove={e => this.mouseMove(e.clientX)}
            style={[styles.progressBar, { backgroundImage: barStyle }]}>
          </div>
        </div>
        <div style={styles.switch}>
          <Switch
            switch={(field, value) => switchFunc(field, value)}
            field="isLeftEar"
            isActived={isLeftEar}
            color="#5CBDE7"
            bgColor="#d6ebf4">
            左耳
          </Switch>
          <Switch
            switch={(field, value) => switchFunc(field, value)}
            field="isRightEar"
            isActived={isRightEar}
            color="#FF0006"
            bgColor="#ee5d61">
            右耳
          </Switch>
        </div>
      </div>
    );
  }
}

SimulatorPlayer.propTypes = {
  left: T.arrayOf(T.number),
  right: T.arrayOf(T.number),
  isRightEar: T.bool,
  isLeftEar: T.bool,
  switchFunc: T.func,
};


export default connect(
  state => ({
    left: state.Simulator.leftResult,
    right: state.Simulator.rightResult,
  }),
)(radium(SimulatorPlayer));



// WEBPACK FOOTER //
// ./containers/Simulators/SimulatorPlayer.js